import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, delay, from, map, of, Subject, switchMap, tap } from 'rxjs';

import {
  ApiAuthorizationType,
  OAuth2CredentialType,
  OAuth2FlowType,
} from '@blaze-shared/api-contracts-domain';
import { urlUtils } from '@blaze-shared/platform-util';
import { SelectComponent } from '@blaze-shared/shared-feature';
import {
  ButtonComponent,
  PasswordVisibilityToggleDirective,
  TooltipDirective,
} from '@blaze-shared/shared-ui';
import { SelectOption } from '@blaze-shared/shared-util';

import { ApiBuilderStore } from '../+state/api-builder.store';
import {
  ApiBuilderCommonConfig,
  ApiOauth2AuthorizationForm,
  GeneralCardForm,
  ParametersForm,
} from '../api-builder.model';
import { OauthAuthorizationFormComponent } from '../oauth-authorization-form/oauth-authorization-form.component';
import { ParametersListComponent } from '../parameters-list/parameters-list.component';

@UntilDestroy()
@Component({
  selector: 'blaze-api-general-card',
  imports: [
    AsyncPipe,
    ButtonComponent,
    FormsModule,
    MatIconModule,
    MatTooltipModule,
    OauthAuthorizationFormComponent,
    ParametersListComponent,
    PasswordVisibilityToggleDirective,
    ReactiveFormsModule,
    SelectComponent,
    TooltipDirective,
  ],
  templateUrl: './api-general-card.component.html',
  styleUrls: ['./api-general-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApiGeneralCardComponent implements OnInit {
  private readonly apiBuilderStore = inject(ApiBuilderStore);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('copyLinkTooltip') copyLinkTooltip!: MatTooltip;

  common!: ApiBuilderCommonConfig;
  formCtrl!: FormGroup<GeneralCardForm>;
  oAuthAuthorized$ = this.apiBuilderStore.oAuthAuthorized$;
  apiAuthorizationTypes!: SelectOption<ApiAuthorizationType>[];
  oauthFlowTypes!: SelectOption<OAuth2FlowType>[];
  readonly AuthorizationType = ApiAuthorizationType;
  readonly oAuth2RedirectUrl;

  copyToClipboard$$ = new Subject<string>();

  get oAuth2Ctrl(): FormGroup<ApiOauth2AuthorizationForm> {
    return this.formCtrl.controls.oAuth2Authorization;
  }

  get headersCtrl(): FormArray<FormGroup<ParametersForm>> {
    return this.formCtrl.controls.headers;
  }

  constructor() {
    this.oAuth2RedirectUrl = `${window.location.origin}/oauth/callback`;
    this.copyToClipboard$$
      .pipe(
        untilDestroyed(this),
        switchMap((text) =>
          from(navigator.clipboard.writeText(text)).pipe(map(() => 'Copied to clipboard!')),
        ),
        catchError(() => of('Oops something went wrong!')),
        tap((message) => {
          this.copyLinkTooltip.message = message;
          this.copyLinkTooltip.show();
        }),
        delay(2000),
      )
      .subscribe(() => {
        this.copyLinkTooltip.hide();
        this.copyLinkTooltip.message = '';
      });
    this.apiAuthorizationTypes = [
      { value: ApiAuthorizationType.NoAuth, label: 'None' },
      { value: ApiAuthorizationType.Basic, label: 'Basic' },
      { value: ApiAuthorizationType.Bearer, label: 'Bearer' },
      { value: ApiAuthorizationType.OAuth2, label: 'OAuth 2.0' },
    ];
    this.oauthFlowTypes = [
      { value: OAuth2FlowType.AuthorizationCode, label: 'Authorization Code' },
      { value: OAuth2FlowType.ClientCredentials, label: 'Client Credentials' },
    ];
  }

  ngOnInit(): void {
    this.formCtrl = this.fb.group({
      baseUrl: new FormControl('', { nonNullable: true }),
      headers: new FormArray<FormGroup<ParametersForm>>([]),
      apiAuthorizationType: new FormControl(ApiAuthorizationType.NoAuth, { nonNullable: true }),
      basicAuthorization: new FormGroup({
        username: new FormControl(''),
        password: new FormControl(''),
      }),
      bearerTokenAuthorization: new FormGroup({
        token: new FormControl(''),
      }),
      oAuth2Authorization: new FormGroup({
        accessToken: new FormControl(''),
        authorizeUrl: new FormControl('', Validators.required),
        clientId: new FormControl('', Validators.required),
        clientSecret: new FormControl('', Validators.required),
        isAuthorized: new FormControl(false, { nonNullable: true }),
        oAuth2CredentialType: new FormControl(OAuth2CredentialType.AuthorizationHeader, {
          nonNullable: true,
        }),
        oAuth2FlowType: new FormControl(OAuth2FlowType.AuthorizationCode, { nonNullable: true }),
        redirectUrl: new FormControl(this.oAuth2RedirectUrl),
        refreshToken: new FormControl(''),
        scope: new FormControl(''),
        tokenUrl: new FormControl('', Validators.required),
      }),
    });

    this.formCtrl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.apiBuilderStore.setCommonConfig(value as ApiBuilderCommonConfig);
    });

    this.apiBuilderStore.common$.pipe(untilDestroyed(this)).subscribe((common) => {
      this.common = common;

      this.formCtrl.patchValue(common, { emitEvent: false });
      this.cdr.detectChanges();
    });
  }

  /**
   * If user types full url with slash or parameters at the end
   * try to extract origin and pathname from it.
   */
  formatUrlField(): void {
    const urlField = this.formCtrl.get('baseUrl');

    let value = '';
    try {
      const url = new URL(urlUtils.addProtocolWhenNeeded(urlField?.value || ''));
      value = `${url.origin}${url.pathname}`;
    } catch {
      value = '';
    }

    if (value) {
      urlField?.patchValue(value);
    }
  }

  isAuthType(type: ApiAuthorizationType): boolean {
    return this.formCtrl.value.apiAuthorizationType === type;
  }

  showEditOAuthForm(): void {
    this.apiBuilderStore.setOAuthAuthorized(false);
  }
}
